import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core'
import { MatChipSelectionChange } from '@angular/material/chips'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs'
import { GbFilterChipConfig, GbFilterChips, GbFilterChipValue } from './gb-filter-chips.models'

@UntilDestroy()
@Component({
  selector: 'gb-filter-chips',
  templateUrl: './gb-filter-chips.component.html',
  styleUrls: ['./gb-filter-chips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbFilterChipsComponent implements OnInit {
  @Input() filters!: GbFilterChips

  @Output() filterChange = new EventEmitter<GbFilterChips>()
  filterChangeSubject = new Subject<GbFilterChips>()

  ngOnInit(): void {
    this.filterChangeSubject
      .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((filters) => this.filterChange.emit(filters))
  }

  isSelected(key: string, value: string | null): boolean {
    return Array.isArray(this.filters[key].value)
      ? this.filters[key].value.includes(value as any)
      : this.filters[key].value === value
  }

  isDisabled(key: string, value: string | null): boolean {
    return Array.isArray(this.filters[key].value)
      ? this.filters[key].value.length === 1 && this.isSelected(key, value)
      : this.isSelected(key, value)
  }

  onFilterChange(event: MatChipSelectionChange, key: string): void {
    if (!event.isUserInput) return

    const updatedFilters = this.getUpdatedFilters(event, key)
    this.filters[key].value = updatedFilters[key].value
    this.filterChangeSubject.next(this.filters)
  }

  private getUpdatedFilters(event: MatChipSelectionChange, key: string): GbFilterChips {
    const newFilters = { ...this.filters }

    if (event.selected) {
      newFilters[key].value = this.addFilter(newFilters[key], event.source.value)
    } else {
      newFilters[key].value = this.removeFilter(newFilters[key], event.source.value)
    }

    return newFilters
  }

  private addFilter(filter: GbFilterChipConfig, value: string | null): GbFilterChipValue {
    return filter.multiple ? [...(filter.value ?? []), value] : value ?? ''
  }

  private removeFilter(filter: GbFilterChipConfig, newValue: string | null): GbFilterChipValue {
    return filter.multiple && Array.isArray(filter.value) ? filter.value.filter((v) => v !== newValue) : ''
  }

  // preserve original property order for filters using keyvalue pipe
  originalOrder = (): number => 0
}
